import { Box } from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { isEditableKeyTarget } from 'features/knowledgeDocumentViewer/components/viewer/shared/viewerHotkeys'
import { useCallback, useEffect, useRef, useState } from 'react'

interface SelectionBox {
  startX: number
  startY: number
  endX: number
  endY: number
}

interface UseShiftDragSelectionProps {
  enabled: boolean
  svgRef: React.RefObject<SVGSVGElement | null>
  boxes: Box[]
  onSelectionComplete: (selectedBoxIds: string[]) => void
  imageNaturalDimensions: { w: number; h: number } | null
}

type NormalizedSelectionBox = {
  x: number
  y: number
  width: number
  height: number
}

/** Structural mouse event shape shared by React and DOM MouseEvent. */
type MouseEventLike = {
  shiftKey: boolean
  clientX: number
  clientY: number
  preventDefault(): void
  stopPropagation(): void
}

function normalizeSelectionBox(
  selectionBox: SelectionBox
): NormalizedSelectionBox {
  return {
    x: Math.min(selectionBox.startX, selectionBox.endX),
    y: Math.min(selectionBox.startY, selectionBox.endY),
    width: Math.abs(selectionBox.endX - selectionBox.startX),
    height: Math.abs(selectionBox.endY - selectionBox.startY),
  }
}

function boxIntersectsSelection(
  box: Box,
  selectionBox: NormalizedSelectionBox
): boolean {
  const selectionRight = selectionBox.x + selectionBox.width
  const selectionBottom = selectionBox.y + selectionBox.height
  const boxRight = box.x + box.w
  const boxBottom = box.y + box.h

  return !(
    boxRight < selectionBox.x ||
    box.x > selectionRight ||
    boxBottom < selectionBox.y ||
    box.y > selectionBottom
  )
}

export function useShiftDragSelection({
  enabled,
  svgRef,
  boxes,
  onSelectionComplete,
  imageNaturalDimensions,
}: UseShiftDragSelectionProps) {
  const [isShiftPressed, setIsShiftPressed] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null)
  const isDrawingRef = useRef(false)
  const selectionBoxRef = useRef<SelectionBox | null>(null)
  const boxesRef = useRef(boxes)
  const onSelectionCompleteRef = useRef(onSelectionComplete)

  selectionBoxRef.current = selectionBox
  boxesRef.current = boxes
  onSelectionCompleteRef.current = onSelectionComplete

  const cancelDrawing = useCallback(() => {
    if (!isDrawingRef.current) return
    isDrawingRef.current = false
    setIsDragging(false)
    setSelectionBox(null)
  }, [])

  const clearShiftMode = useCallback(() => {
    setIsShiftPressed(false)
    cancelDrawing()
  }, [cancelDrawing])

  const syncShiftFromMouseEvent = useCallback(
    (e: { shiftKey: boolean }) => {
      setIsShiftPressed((wasPressed) =>
        wasPressed === e.shiftKey ? wasPressed : e.shiftKey
      )
      if (!e.shiftKey) {
        cancelDrawing()
      }
    },
    [cancelDrawing]
  )

  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Shift') return
      // Shift+Enter / capital typing in search must not arm marquee mode.
      if (isEditableKeyTarget(e.target)) return
      setIsShiftPressed(true)
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key !== 'Shift') return
      clearShiftMode()
    }

    // Missed keyups are common after focus changes (search, page nav, alt-tab).
    const handleWindowBlur = () => {
      clearShiftMode()
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearShiftMode()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    window.addEventListener('blur', handleWindowBlur)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('blur', handleWindowBlur)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled, clearShiftMode])

  const convertScreenToImageCoordinates = useCallback(
    (clientX: number, clientY: number) => {
      if (!svgRef.current || !imageNaturalDimensions) {
        return null
      }

      const svg = svgRef.current
      const pt = svg.createSVGPoint()
      pt.x = clientX
      pt.y = clientY

      // Transform the point from screen coordinates to SVG coordinates
      const screenCTM = svg.getScreenCTM()
      if (!screenCTM) return null

      const svgCoords = pt.matrixTransform(screenCTM.inverse())

      return { x: svgCoords.x, y: svgCoords.y }
    },
    [svgRef, imageNaturalDimensions]
  )

  const finishSelection = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDrawingRef.current) return

      const currentSelection = selectionBoxRef.current
      isDrawingRef.current = false
      setIsDragging(false)

      if (!currentSelection) {
        setSelectionBox(null)
        return
      }

      const endCoords = convertScreenToImageCoordinates(clientX, clientY)
      const finalSelection = endCoords
        ? {
            ...currentSelection,
            endX: endCoords.x,
            endY: endCoords.y,
          }
        : currentSelection

      const normalizedSelectionBox = normalizeSelectionBox(finalSelection)
      const selectedIds = boxesRef.current
        .filter((box) => boxIntersectsSelection(box, normalizedSelectionBox))
        .map((box) => box.id)

      if (selectedIds.length > 0) {
        onSelectionCompleteRef.current(selectedIds)
      }

      setSelectionBox(null)
    },
    [convertScreenToImageCoordinates]
  )

  // Complete or cancel marquee even if the pointer is released outside the SVG.
  useEffect(() => {
    const handleWindowMouseUp = (e: MouseEvent) => {
      if (!isDrawingRef.current) return
      finishSelection(e.clientX, e.clientY)
    }

    window.addEventListener('mouseup', handleWindowMouseUp)
    return () => {
      window.removeEventListener('mouseup', handleWindowMouseUp)
    }
  }, [finishSelection])

  const handleMouseDown = useCallback(
    (e: MouseEventLike) => {
      // Prefer the event modifier over latched key state so a missed keyup
      // cannot permanently steal pan from the viewer.
      syncShiftFromMouseEvent(e)
      if (!e.shiftKey || !imageNaturalDimensions) return

      const coords = convertScreenToImageCoordinates(e.clientX, e.clientY)
      if (!coords) return

      isDrawingRef.current = true
      setIsDragging(true)
      setSelectionBox({
        startX: coords.x,
        startY: coords.y,
        endX: coords.x,
        endY: coords.y,
      })

      e.preventDefault()
      e.stopPropagation()
    },
    [
      imageNaturalDimensions,
      convertScreenToImageCoordinates,
      syncShiftFromMouseEvent,
    ]
  )

  const handleMouseMove = useCallback(
    (e: MouseEventLike) => {
      syncShiftFromMouseEvent(e)

      if (!isDrawingRef.current) return

      const coords = convertScreenToImageCoordinates(e.clientX, e.clientY)
      if (!coords) return

      setSelectionBox((prev) =>
        prev
          ? {
              ...prev,
              endX: coords.x,
              endY: coords.y,
            }
          : null
      )

      e.preventDefault()
      e.stopPropagation()
    },
    [convertScreenToImageCoordinates, syncShiftFromMouseEvent]
  )

  const handleMouseUp = useCallback(
    (e: MouseEventLike) => {
      if (!isDrawingRef.current) return

      finishSelection(e.clientX, e.clientY)

      e.preventDefault()
      e.stopPropagation()
    },
    [finishSelection]
  )

  const normalizedSelectionBox = selectionBox
    ? normalizeSelectionBox(selectionBox)
    : null

  return {
    isShiftPressed,
    isDragging,
    selectionBox: normalizedSelectionBox,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  }
}
